/*
  Smooth open/close for accordion-style <details> (collapsible content,
  product tabs, disclosures, FAQs, footer columns, cart note). Animates the
  <details> height so content margins are included; dropdowns and modals
  (header menus, filters, search) are not matched and keep native behaviour.
*/
(() => {
  const SELECTOR = [
    '.accordion > details',
    '.accordion > .complementary-products__container > details',
    '.disclosures__details',
    '.product-faqs__item',
    '#Details-CartDrawer',
  ].join(',');
  const DURATION = 300;
  const EASING = 'ease-out';
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const running = new WeakMap();

  function closedHeight(details, summary) {
    const d = getComputedStyle(details);
    const s = getComputedStyle(summary);
    return (
      summary.offsetHeight +
      parseFloat(s.marginTop) +
      parseFloat(s.marginBottom) +
      parseFloat(d.paddingTop) +
      parseFloat(d.paddingBottom) +
      parseFloat(d.borderTopWidth) +
      parseFloat(d.borderBottomWidth)
    );
  }

  // Space the open <details> takes in the flow. In block layout the content's
  // last margin collapses out of it; overflow: hidden (set while animating)
  // would trap that margin, so measure up to where the next box starts.
  function openExtent(details) {
    const parentDisplay = details.parentElement ? getComputedStyle(details.parentElement).display : '';
    if (/flex|grid/.test(parentDisplay)) return details.offsetHeight;

    const probe = document.createElement('div');
    probe.style.cssText = 'display:block;height:0;padding:1px 0 0;margin:0;border:0;visibility:hidden';
    details.after(probe);
    const extent = probe.getBoundingClientRect().top - details.getBoundingClientRect().top;
    probe.remove();
    return extent;
  }

  function toggle(details, summary) {
    const current = running.get(details);
    const opening = current ? !current.opening : !details.open;
    const startHeight = current ? details.offsetHeight : openExtent(details);

    if (current) current.animation.cancel();

    details.style.overflow = 'hidden';
    details.style.height = '';
    if (opening) {
      details.style.overflow = '';
      details.open = true;
    }
    const endHeight = opening ? openExtent(details) : closedHeight(details, summary);
    details.style.overflow = 'hidden';
    details.style.height = `${startHeight}px`;

    if (summary.hasAttribute('aria-expanded')) summary.setAttribute('aria-expanded', opening);

    const animation = details.animate(
      { height: [`${startHeight}px`, `${endHeight}px`] },
      { duration: DURATION, easing: EASING }
    );
    running.set(details, { animation, opening });

    animation.onfinish = () => {
      running.delete(details);
      if (!opening) details.open = false;
      details.style.height = '';
      details.style.overflow = '';
    };
  }

  document.addEventListener('click', (event) => {
    const summary = event.target.closest('summary');
    if (!summary || event.defaultPrevented) return;

    const details = summary.parentElement;
    if (!details || details.tagName !== 'DETAILS' || !details.matches(SELECTOR)) return;
    if (reducedMotion.matches || typeof details.animate !== 'function') return;
    // Footer columns on desktop are always shown; their summary is inert.
    if (getComputedStyle(summary).pointerEvents === 'none') return;

    event.preventDefault();
    toggle(details, summary);
  });
})();
